import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import logo from '../../images/Skyline-Contractors-final-logo.png';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'ABOUT US', path: '/about' },
    {
      name: 'OUR STRENGTH',
      dropdown: [
        { name: 'Employees', path: '/OurEmployes' },
        { name: 'Assets', path: '/OurStrength' }
      ]
    },
    {
      name: 'PROJECTS',
      dropdown: [
        { name: 'Completed', path: '/completed' },
        { name: 'On Going', path: '/OnGoing' }
      ]
    },
    /* 
    Temporarily commented out Management section
    { 
      name: 'MANAGEMENT', 
      dropdown: [
        { name: 'About Skyline', path: '/AboutCompany' },
        { name: 'Key Person', path: '/keyperson' }
      ]
    },
    */
    { name: 'GALLERY', path: '/PrevGallery' },
    { name: 'CAREER', path: '/career' },
    { name: 'CONTACT', path: '/contact' }
  ];

  // Toggle dropdown for mobile and desktop click
  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-skyline-primary/95 backdrop-blur-md shadow-lg py-1' : 'bg-skyline-primary/80 backdrop-blur-md py-2'}`}>
      <div className="container mx-auto px-6 max-w-7xl flex items-center justify-between">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <div className="bg-white p-1.5 rounded-lg">
            <img src={logo} alt="Skyline Construction Logo" className="h-8 md:h-10 w-auto object-contain" />
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <div
              key={link.name}
              className="relative"
              onMouseEnter={() => link.dropdown && setOpenDropdown(link.name)}
              onMouseLeave={() => link.dropdown && setOpenDropdown(null)}
            >
              {link.dropdown ? (
                <>
                  <div className="flex items-center gap-1 text-sm font-semibold tracking-wide text-white hover:text-skyline-accent cursor-pointer transition-colors duration-200 uppercase py-2">
                    {link.name} <ChevronDown size={14} className={`transition-transform duration-300 ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                  </div>
                  {/* Dropdown panel */}
                  <div className={`absolute top-full left-0 pt-2 w-48 transition-all duration-300 z-50 ${openDropdown === link.name ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}>
                    <div className="bg-skyline-secondary rounded-lg shadow-xl overflow-hidden border border-gray-700">
                      {link.dropdown.map((drop) => (
                        <Link
                          key={drop.name}
                          to={drop.path}
                          className="block px-4 py-3 text-sm text-gray-200 hover:text-skyline-accent hover:bg-skyline-primary transition-colors"
                        >
                          {drop.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <Link
                  to={link.path}
                  className={`text-sm font-semibold tracking-wide uppercase transition-colors duration-200 py-2 ${pathname === link.path ? 'text-skyline-accent' : 'text-white hover:text-skyline-accent'}`}
                >
                  {link.name}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden text-white p-2 hover:bg-white/10 rounded-md transition-colors"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-skyline-primary border-t border-skyline-secondary overflow-hidden"
          >
            <div className="container mx-auto px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.dropdown ? (
                    <div>
                      <button
                        onClick={() => toggleDropdown(link.name)}
                        className="w-full flex items-center justify-between py-3 text-sm font-semibold text-white uppercase tracking-wider hover:text-skyline-accent transition-colors"
                      >
                        {link.name}
                        <ChevronRight size={16} className={`transition-transform duration-200 ${openDropdown === link.name ? 'rotate-90' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {openDropdown === link.name && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-4 flex flex-col gap-1 border-l-2 border-skyline-accent mb-2 overflow-hidden"
                          >
                            {link.dropdown.map((drop) => (
                              <Link
                                key={drop.name}
                                to={drop.path}
                                className="block py-2 text-gray-300 hover:text-skyline-accent transition-colors"
                              >
                                {drop.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className={`block py-3 font-semibold uppercase tracking-wider text-sm ${pathname === link.path ? 'text-skyline-accent' : 'text-white hover:text-skyline-accent'}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
